/**
 * SubmissionTable Component
 *
 * Renders the submissions table with:
 * - Checkbox selection column
 * - Submission data columns
 * - Pagination controls
 *
 * Follows Single Responsibility Principle - only handles table display
 */

import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { getSubmissionName, getSubmissionEmail } from "@/utils/submissionHelpers";
import type { SubmissionItem } from "../store/types";

interface SubmissionTableProps {
  /** Submissions to display (paginated) */
  submissions: SubmissionItem[];
  /** Total count of filtered submissions */
  totalCount: number;
  /** Current page number */
  currentPage: number;
  /** Items per page */
  pageSize: number;
  /** Total number of pages */
  totalPages: number;
  /** Callback when page changes */
  onPageChange: (page: number) => void;
  /** Callback when page size changes */
  onPageSizeChange: (size: number) => void;
  /** Callback when row is clicked */
  onRowClick: (submission: SubmissionItem) => void;
  /** Whether a submission is selected */
  isSelected: (id: number) => boolean;
  /** Callback to toggle selection */
  onToggleSelection: (id: number) => void;
  /** Get select all checkbox state */
  selectAllState: boolean | "indeterminate";
  /** Callback to toggle select all */
  onToggleSelectAll: () => void;
  /** Whether more data can be loaded from server */
  hasNextPage?: boolean;
  /** Whether loading more data */
  isFetchingNextPage?: boolean;
  /** Callback to load more data from server */
  onLoadMore?: () => void;
  /** Total loaded from server (before client filtering) */
  loadedCount?: number;
  /** Total available in server */
  serverTotalCount?: number;
}

const PAGE_SIZE_OPTIONS = [5, 10, 15, 20, 25, 50, 100];

/**
 * Submissions table with pagination
 *
 * @example
 * ```tsx
 * <SubmissionTable
 *   submissions={paginatedSubmissions}
 *   totalCount={filteredSubmissions.length}
 *   currentPage={currentPage}
 *   pageSize={pageSize}
 *   totalPages={totalPages}
 *   onPageChange={setCurrentPage}
 *   onPageSizeChange={setPageSize}
 *   onRowClick={(s) => navigate(`/admin/submissions/${s.id}`)}
 *   isSelected={isSelected}
 *   onToggleSelection={toggleSelection}
 *   selectAllState={getSelectAllState(paginatedSubmissions)}
 *   onToggleSelectAll={() => toggleSelectAll(paginatedSubmissions)}
 * />
 * ```
 */
export function SubmissionTable({
  submissions,
  totalCount,
  currentPage,
  pageSize,
  totalPages,
  onPageChange,
  onPageSizeChange,
  onRowClick,
  isSelected,
  onToggleSelection,
  selectAllState,
  onToggleSelectAll,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  loadedCount,
  serverTotalCount,
}: SubmissionTableProps) {
  // Calculate display range
  const startIndex = totalCount > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const endIndex = Math.min(currentPage * pageSize, totalCount);

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages: number[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else if (currentPage <= 3) {
      for (let i = 1; i <= maxVisible; i++) pages.push(i);
    } else if (currentPage >= totalPages - 2) {
      for (let i = totalPages - maxVisible + 1; i <= totalPages; i++)
        pages.push(i);
    } else {
      for (let i = currentPage - 2; i <= currentPage + 2; i++) pages.push(i);
    }

    return pages;
  };

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-12">
              <Checkbox
                checked={selectAllState}
                onCheckedChange={onToggleSelectAll}
                aria-label="Seleccionar todos de esta página"
              />
            </TableHead>
            <TableHead>ID</TableHead>
            <TableHead>Nombre</TableHead>
            <TableHead>Tipo</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Fecha</TableHead>
            <TableHead>Estado</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {submissions.map((submission) => (
            <TableRow
              key={submission.id}
              className={cn(
                "cursor-pointer hover:bg-muted",
                isSelected(submission.db_id) && "bg-primary/5"
              )}
              onClick={() => onRowClick(submission)}
            >
              <TableCell onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  checked={isSelected(submission.db_id)}
                  onCheckedChange={() => onToggleSelection(submission.db_id)}
                  aria-label={`Seleccionar ${getSubmissionName(submission)}`}
                />
              </TableCell>
              <TableCell>{submission.db_id}</TableCell>
              <TableCell>{getSubmissionName(submission)}</TableCell>
              <TableCell>
                {submission.is_referral ? (
                  <Badge
                    variant="outline"
                    className="bg-blue-100 text-blue-800"
                  >
                    Referido
                  </Badge>
                ) : (
                  <Badge
                    variant="secondary"
                    className="bg-green-100 text-green-800"
                  >
                    Principal
                  </Badge>
                )}
              </TableCell>
              <TableCell>{getSubmissionEmail(submission)}</TableCell>
              <TableCell>
                {new Date(submission.created_at).toLocaleDateString()}
              </TableCell>
              <TableCell>
                <Badge
                  variant="outline"
                  className={
                    submission.status === "ACTIVE"
                      ? "bg-green-100 text-green-800"
                      : "bg-gray-100 text-gray-800"
                  }
                >
                  {submission.status}
                </Badge>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Pagination */}
      <div className="flex items-center justify-between border-t pt-4">
        <div className="flex items-center gap-4">
          <p className="text-sm text-muted-foreground">
            Mostrando {startIndex} - {endIndex} de {totalCount}
            {serverTotalCount !== undefined && serverTotalCount > totalCount && (
              <span className="text-muted-foreground/70">
                {" "}({loadedCount} cargados de {serverTotalCount})
              </span>
            )}
          </p>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Por página:</span>
            <Select
              value={String(pageSize)}
              onValueChange={(value) => onPageSizeChange(Number(value))}
            >
              <SelectTrigger className="w-[70px] h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <SelectItem key={size} value={String(size)}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Previous button */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1 || isFetchingNextPage}
          >
            <ChevronLeft className="h-4 w-4" />
            Anterior
          </Button>

          {/* Page numbers */}
          {totalPages > 1 && (
            <div className="flex items-center gap-1">
              {getPageNumbers().map((pageNum) => (
                <Button
                  key={pageNum}
                  variant={currentPage === pageNum ? "default" : "outline"}
                  size="sm"
                  className="w-8 h-8 p-0"
                  onClick={() => onPageChange(pageNum)}
                  disabled={isFetchingNextPage}
                >
                  {pageNum}
                </Button>
              ))}
            </div>
          )}

          {/* Next / Load More button */}
          {currentPage === totalPages && hasNextPage && onLoadMore ? (
            <Button
              variant="outline"
              size="sm"
              onClick={onLoadMore}
              disabled={isFetchingNextPage}
            >
              {isFetchingNextPage ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  Cargando...
                </>
              ) : (
                <>
                  Cargar más
                  <ChevronRight className="h-4 w-4" />
                </>
              )}
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages || isFetchingNextPage}
            >
              Siguiente
              <ChevronRight className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </>
  );
}
